import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import { getTextField } from '@lender/utils';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowCheckIcon, TriangleIcon } from '@sharedCR/coreDesignSystem/icons';
import { ButtonGroupWrapper } from 'app/shared/componentsRestyled/coreDesignSystem/Buttons';
import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { array, object } from 'yup';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';
import { FeeCicReasonType } from 'constants/loanApplication';
import { HORIZONTAL_ALIGNMENT } from 'constants/ui';
import useForm from 'hooks/useForm';

import { closeDialogAction, showMessageAction } from 'store/global/actions';
import { selectCicChangeReasons } from 'store/loanApplications/selectors';

import { CIC_REASON_MODAL_CONTROLS_ID, NOTE_MAX_LENGTH } from './CicReasonFormModal.constants';
import {
  accordionStylesSx,
  tableBodyStylesSx,
  tableContainerStylesSx,
  tableHeadStylesSx,
} from './CicReasonFormModal.styles';
import { CicReasonFormModalTestIds } from './CicReasonFormModal.testIds';
import {
  getHasShownFeeReasons,
  getInitialFormData,
  getIsFeeShown,
  handleReasonChange,
} from './CicReasonFormModal.utils';
import CicReasonFormModalNoteRow from './CicReasonFormModalNoteRow';

const schema = array().of(
  object().shape({
    reasons: getTextField({ isRequired: false }).nullable(),
    notes: array().of(getTextField().max(NOTE_MAX_LENGTH).nullable()),
  }),
);

const CicReasonFormModal = ({
  formRef,
  actionData,
  isLoading,
  setIsLoading,
  action,
  isCicReasonButtonTriggered,
}) => {
  const dispatch = useDispatch();

  const [targetControlsElement, setTargetControlsElement] = useState(null);

  const feesWithReason = useSelector(selectCicChangeReasons);

  const initialFormValues = useMemo(
    () => getInitialFormData(actionData.changes, { feesWithReason }),
    [actionData, feesWithReason],
  );

  const hasInitialReasons = getHasShownFeeReasons(initialFormValues, {
    isCicReasonButtonTriggered,
  });

  const {
    form,
    validationErrors,
    handleChange,
    setInForm,
    setSchema,
    validateField,
    setForm,
    isFormChanged,
    validateForm,
  } = useForm(initialFormValues);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  useEffect(() => {
    const elem = document.getElementById(CIC_REASON_MODAL_CONTROLS_ID);
    setTargetControlsElement(elem);
  }, []);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      const isSkip = e.currentTarget?.dataset.isSkip === 'true';
      const hasEmptyNote = _.some(
        form,
        fee =>
          getIsFeeShown(fee, { isCicReasonButtonTriggered }) &&
          _.some(fee.reasons, (__, reasonIndex) => !fee.notes[reasonIndex]),
      );

      if (!isSkip && hasEmptyNote) {
        return dispatch(showMessageAction({ message: "Note can't be empty", variant: 'error' }));
      }

      // Save stays enabled for an untouched form with reasons; accepting them must still submit.
      if (!isSkip && !(await validateForm({ noFormChangedValidate: true }))) {
        return;
      }

      setIsLoading(true);

      await action({
        ...actionData,
        isSkip,
        changes: form.map((fee, index) => {
          const reasons = fee.reasons?.map(({ value }, reasonIndex) => ({
            cicFeeReasonId: value,
            note: fee.notes[reasonIndex],
          }));

          return {
            fee: actionData.changes[index].fee,
            reasons,
            credits: actionData.changes[index]?.credits,
            type:
              _.isEmpty(reasons) || isSkip
                ? FeeCicReasonType.WITHOUT_REASON
                : FeeCicReasonType.WITH_CIC_REASON,
          };
        }),
      })
        .then(() => dispatch(closeDialogAction()))
        .finally(() => setIsLoading(false));
    },
    [dispatch, setIsLoading, form, action, actionData, validateForm, isCicReasonButtonTriggered],
  );

  const onCancel = useCallback(() => dispatch(closeDialogAction()), [dispatch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onReasonChange = useCallback(handleReasonChange({ setForm, setInForm, validateField }), [
    setInForm,
    validateField,
    setForm,
  ]);

  return (
    <form
      noValidate
      className="pt-12 w-full"
      data-ischanged={isFormChanged}
      ref={formRef}
      onSubmit={onSubmit}
    >
      <Typography className="italic" variant="regular14">
        <TriangleIcon size={IconSize[12]} />
        {` Select at least one reason per fee and enter a note.`}
      </Typography>

      <div className="my-20">
        {form.map((fee, feeIndex) => {
          const name = `[${feeIndex}].reasons`;
          const { feeTypeId, feeTypeName, availableReasons, reasons = [] } = fee;

          if (!getIsFeeShown(fee, { isCicReasonButtonTriggered })) {
            return null;
          }

          return (
            <Accordion defaultExpanded key={feeTypeId} sx={accordionStylesSx}>
              <AccordionSummary
                aria-controls={`panel${feeTypeId}-content`}
                expandIcon={<ArrowCheckIcon direction={IconDirection.DOWN} />}
              >
                <Typography variant="bold16">{feeTypeName}</Typography>
              </AccordionSummary>

              <AccordionDetails>
                <Divider />

                <div className="mt-8 w-360">
                  <AutocompleteCDS
                    multiple
                    dataTestIdInput={
                      CicReasonFormModalTestIds.CIC_REASON_FORM_SELECT_A_REASON_INPUT
                    }
                    dataTestIdOption={
                      CicReasonFormModalTestIds.CIC_REASON_FORM_SELECT_A_REASON_OPTION
                    }
                    disabled={isLoading}
                    error={!!_.get(validationErrors, name)}
                    helperText={_.get(validationErrors, name)}
                    label="Select a reason"
                    name={name}
                    options={availableReasons}
                    value={_.get(form, name, [])}
                    variant="outlined"
                    onChange={onReasonChange}
                  />
                </div>

                {!_.isEmpty(reasons) && (
                  <TableContainer sx={tableContainerStylesSx}>
                    <Table>
                      <TableHead sx={tableHeadStylesSx}>
                        <TableRow>
                          <TableCell>CIC Reason(s)</TableCell>
                          <TableCell>Notes</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody sx={tableBodyStylesSx}>
                        {reasons.map((i, reasonIndex) => {
                          const notePath = `[${feeIndex}].notes[${reasonIndex}]`;

                          return (
                            <TableRow key={i.value}>
                              <TableCell align="left">{i.name}</TableCell>
                              <TableCell align="left">
                                <CicReasonFormModalNoteRow
                                  errorMessage={_.get(validationErrors, notePath)}
                                  feeTypeId={feeTypeId}
                                  isLoading={isLoading}
                                  notePath={notePath}
                                  noteValue={_.get(form, notePath)}
                                  reason={i}
                                  setInForm={setInForm}
                                  onNoteChange={handleChange}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </div>

      {!!targetControlsElement &&
        createPortal(
          <ButtonGroupWrapper alignment={HORIZONTAL_ALIGNMENT.RIGHT} className="w-full px-4">
            <Button
              data-testid={CicReasonFormModalTestIds.CIC_REASON_FORM_CANCEL_BUTTON}
              disabled={isLoading}
              size="large"
              variant="line"
              onClick={onCancel}
            >
              Cancel
            </Button>

            <Button
              data-is-skip="true"
              data-testid={CicReasonFormModalTestIds.CIC_REASON_FORM_SKIP_BUTTON}
              disabled={isLoading}
              size="large"
              variant="line"
              onClick={onSubmit}
            >
              Skip
            </Button>

            <Button
              data-is-skip="false"
              data-testid={CicReasonFormModalTestIds.CIC_REASON_FORM_SAVE_BUTTON}
              disabled={isLoading || (!isFormChanged && !hasInitialReasons)}
              size="large"
              onClick={onSubmit}
            >
              Save
            </Button>
          </ButtonGroupWrapper>,
          targetControlsElement,
        )}
    </form>
  );
};

CicReasonFormModal.propTypes = {
  ...WithLoadingPropTypes,
  formRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  actionData: PropTypes.object.isRequired,
  action: PropTypes.func.isRequired,
  isCicReasonButtonTriggered: PropTypes.bool,
};

export default withLoading(CicReasonFormModal);
